// Consolidated mock data for invoice panels
// This file consolidates all mock data from the invoice constants files

import type { EventData } from '../types/events.types';
import type { PaymentDetail, BaseTimelineEvent } from '../components/panels/types/panel-common.types';
import type { InvoiceAccountingEntry, InvoicePayment, InvoiceCreditNote } from '../components/panels/invoices/types';

// ===== EVENTS DATA =====

export function generateInvoiceTimelineEvents(_invoiceData?: unknown): BaseTimelineEvent[] {
  return [
    {
      id: 'inv_001',
      title: 'Invoice #INV93u4239u4 sent to Customer name',
      timestamp: '10/9/25, 2:56:00 PM',
      details: []
    },
    {
      id: 'inv_002',
      title: 'Customer name signed off the contract',
      timestamp: '10/9/25, 2:56:00 PM',
      details: []
    },
    {
      id: 'inv_003',
      title: 'Billing schedule created',
      timestamp: '9/30/25, 2:56:00 PM',
      details: []
    }
  ];
}

// ===== INVOICE EVENTS DATA =====

export const SAMPLE_INVOICE_EVENTS: EventData[] = [
  {
    id: 'inv_evt_1',
    eventType: 'payment.received',
    description: 'Full payment of €24,900.00 received via bank transfer from TechnoSoft Ltd.',
    timestamp: '1/15/25, 2:30:00 PM',
    requestId: 'req_inv_payment_xyz789',
    status: 'succeeded'
  },
  {
    id: 'inv_evt_2',
    eventType: 'payment.confirmed',
    description: 'Payment confirmation sent to customer and finance team via email notification',
    timestamp: '1/15/25, 2:35:00 PM',
    requestId: 'req_inv_confirm_abc123',
    status: 'succeeded'
  },
  {
    id: 'inv_evt_3',
    eventType: 'reminder.sent',
    description: 'Final payment reminder sent to accounting@technosoft.bg - Invoice overdue by 3 days',
    timestamp: '1/14/25, 2:30:00 PM',
    requestId: 'req_inv_reminder_def456',
    status: 'succeeded'
  },
  {
    id: 'inv_evt_4',
    eventType: 'invoice.viewed',
    description: 'Customer opened invoice email and viewed PDF for 3 minutes via mobile device',
    timestamp: '1/13/25, 9:15:00 AM',
    requestId: 'req_inv_view_ghi789',
    status: 'verified'
  },
  {
    id: 'inv_evt_5',
    eventType: 'invoice.overdue',
    description: 'Invoice INV-2025-001 became overdue. Late fee of €125.00 automatically applied',
    timestamp: '1/12/25, 11:59:59 PM',
    requestId: 'req_inv_overdue_jkl012',
    status: 'processing'
  },
  {
    id: 'inv_evt_6',
    eventType: 'reminder.scheduled',
    description: 'Automated payment reminder scheduled for 3 days after due date per customer preferences',
    timestamp: '1/10/25, 12:00:00 AM',
    requestId: 'req_inv_schedule_mno345',
    status: 'approved'
  },
  {
    id: 'inv_evt_7',
    eventType: 'invoice.sent',
    description: 'Invoice sent to customer via email (accounting@technosoft.bg) and SMS backup notification',
    timestamp: '1/5/25, 10:00:00 AM',
    requestId: 'req_inv_send_pqr678',
    status: 'succeeded'
  },
  {
    id: 'inv_evt_8',
    eventType: 'pdf.generated',
    description: 'Invoice PDF generated with watermark and digital signature. File size: 247KB',
    timestamp: '1/5/25, 9:58:00 AM',
    requestId: 'req_inv_pdf_stu901',
    status: 'succeeded'
  },
  {
    id: 'inv_evt_9',
    eventType: 'invoice.finalized',
    description: 'Invoice approved by finance team. Invoice number INV-2025-001 assigned and locked',
    timestamp: '1/4/25, 5:30:00 PM',
    requestId: 'req_inv_final_vwx234',
    status: 'approved'
  },
  {
    id: 'inv_evt_10',
    eventType: 'tax.calculated',
    description: 'VAT calculation completed: €4,150.00 (20% rate) applied for Bulgaria tax location',
    timestamp: '1/4/25, 3:52:00 PM',
    requestId: 'req_inv_tax_yz567',
    status: 'verified'
  },
  {
    id: 'inv_evt_11',
    eventType: 'line_items.added',
    description: 'Added 5 line items totaling €20,750.00 before tax. Items: Software licenses, Support services',
    timestamp: '1/4/25, 3:48:00 PM',
    requestId: 'req_inv_items_abc890',
    status: 'succeeded'
  },
  {
    id: 'inv_evt_12',
    eventType: 'invoice.created',
    description: 'Invoice drafted for TechnoSoft Ltd. Billing period: February 1-29, 2024',
    timestamp: '1/4/25, 3:45:00 PM',
    requestId: 'req_inv_create_def123',
    status: 'succeeded'
  },
  {
    id: 'inv_evt_13',
    eventType: 'webhook.failed',
    description: 'Invoice status webhook delivery failed with HTTP 503 error. Merchant endpoint unavailable. Retry scheduled in 15 minutes.',
    timestamp: '1/15/25, 2:32:00 PM',
    requestId: 'req_inv_webhook_fail_456',
    status: 'failed'
  },
  {
    id: 'inv_evt_14',
    eventType: 'customer.notified',
    description: 'Customer notification sent via preferred channels: Email primary, SMS backup for payment due in 7 days',
    timestamp: '1/8/25, 9:00:00 AM',
    requestId: 'req_inv_notify_789',
    status: 'succeeded'
  },
  {
    id: 'inv_evt_15',
    eventType: 'compliance.check',
    description: 'Anti-money laundering and tax compliance verification completed for high-value invoice processing',
    timestamp: '1/4/25, 4:15:00 PM',
    requestId: 'req_inv_compliance_012',
    status: 'approved'
  }
];

// ===== GENERAL SECTION DATA =====

const formatDate = (value: string | Date | undefined): string => {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const CURRENCY_LABELS: Record<string, string> = {
  USD: 'USD - US Dollar',
  EUR: 'EUR - Euro',
  GBP: 'GBP - British Pound',
  BGN: 'BGN - Bulgarian Lev',
};

export const generateInvoiceGeneralData = (invoiceData?: import('@shared/types/invoices').Invoice): PaymentDetail[] => {
  if (!invoiceData) {
    // Fallback mock data when no invoice is provided
    return [
      { label: 'Customer', value: 'Unknown', isCopyable: false },
      { label: 'Invoice number', value: '-', isCopyable: true },
      { label: 'Currency', value: 'USD - US Dollar', isCopyable: false },
      { label: 'Created', value: '-', isCopyable: false },
      { label: 'Due', value: '-', isCopyable: false },
    ];
  }

  const currency = invoiceData.currency || 'USD';

  return [
    {
      label: 'Customer',
      value: invoiceData.customerName || 'Unknown',
      isCopyable: false
    },
    {
      label: 'Invoice number',
      value: invoiceData.invoiceNumber || '-',
      isCopyable: true
    },
    {
      label: 'Currency',
      value: CURRENCY_LABELS[currency] || currency,
      isCopyable: false
    },
    {
      label: 'Billing method',
      value: 'Send invoice',
      isCopyable: false
    },
    {
      label: 'Applied balance',
      value: `$${invoiceData.total.toFixed(2)} ${currency}`,
      isCopyable: false
    },
    {
      label: 'Created',
      value: formatDate(invoiceData.created),
      isCopyable: false
    },
    ...(invoiceData.finalizationDate ? [{
      label: 'Finalized',
      value: formatDate(invoiceData.finalizationDate),
      isCopyable: false
    }] : []),
    ...(invoiceData.sentAt ? [{
      label: 'Sent',
      value: formatDate(invoiceData.sentAt),
      isCopyable: false
    }] : []),
    {
      label: 'Due',
      value: formatDate(invoiceData.due),
      isCopyable: false
    },
    ...(invoiceData.notes ? [{
      label: 'Invoice memo',
      value: invoiceData.notes,
      isCopyable: false
    }] : []),
    {
      label: 'Frequency',
      value: invoiceData.frequency,
      isCopyable: false
    },
  ];
};

// ===== ACCOUNTING DATA =====

export const INVOICE_ACCOUNTING_DATA: InvoiceAccountingEntry[] = [
  {
    id: '1',
    account: 'Accounts receivable',
    total: 0.00,
    octStart: 20.00,
    octEnd: 0.00,
    ending: 0.00,
    pending: 0.00
  },
  {
    id: '2',
    account: 'Customer balance',
    total: -68.00,
    octStart: -68.00,
    octEnd: 0.00,
    ending: -68.00,
    pending: 0.00
  },
  {
    id: '3',
    account: 'Deferred revenue',
    total: 0.00,
    octStart: 5.30,
    octEnd: -5.30,
    ending: 0.00,
    pending: 0.00
  },
  {
    id: '4',
    account: 'Revenue',
    total: 8.00,
    octStart: 2.70,
    octEnd: 5.30,
    ending: 8.00,
    pending: 0.00
  }
];

// ===== PAYMENTS DATA =====

export const INVOICE_PAYMENTS_DATA: InvoicePayment[] = [
  {
    id: '1',
    amount: 20.00,
    currency: 'USD',
    status: 'draft',
    description: 'INV-DRAFT',
    date: undefined
  },
  {
    id: '2',
    amount: 20.00,
    currency: 'USD',
    status: 'paid',
    description: 'INV00068',
    date: new Date('2024-10-10')
  },
  {
    id: '3',
    amount: 20.00,
    currency: 'USD',
    status: 'partially_paid',
    description: 'INV00068',
    date: new Date('2024-10-20')
  }
];

// ===== CREDIT NOTES DATA =====

export const INVOICE_CREDIT_NOTES_DATA: InvoiceCreditNote[] = [
  {
    id: '1',
    creditNoteNumber: 'CN-00068',
    total: 2500.00,
    currency: 'USD',
    status: 'open',
    reason: 'INV00068',
    effectiveDate: undefined,
    created: new Date('2024-10-15')
  },
  {
    id: '2',
    creditNoteNumber: 'CN-00068',
    total: 20.00,
    currency: 'USD',
    status: 'canceled',
    reason: 'INV00068',
    effectiveDate: undefined,
    created: new Date('2024-10-20')
  }
];
